const INFRASTRUCTURE_SUBDOMAIN_PREFIXES = [
    'tenant',
    'app',
    'api',
    'backoffice',
    'www3',
    'www',
    'staging',
    'logs',
] as const;

/** Roots where dashboard / infra hosts live (not customer storefronts). */
const KNOWN_APP_ROOTS = ['fleetwire.io', 'fleetwire.test'] as const;

/**
 * First-party registrable domains that host tenant storefronts as `{slug}.root`.
 * Includes the store domain so cart create resolves company the same way on .shop.
 */
const FIRST_PARTY_TENANT_ROOTS = ['fleetwire.io', 'fleetwire.test', 'fleetwire.shop'] as const;

const normalizeHost = (host: string): string => host.trim().toLowerCase().split(':')[0] ?? '';

export const isDashboardAppHost = (host: string): boolean => {
    const hostname = normalizeHost(host);

    for (const root of KNOWN_APP_ROOTS) {
        if (hostname === root) {
            return true;
        }

        if (!hostname.endsWith(`.${root}`)) {
            continue;
        }

        const subdomain = hostname.slice(0, -(root.length + 1));

        return (INFRASTRUCTURE_SUBDOMAIN_PREFIXES as readonly string[]).includes(subdomain);
    }

    return false;
};

/**
 * True when the app is being served from the local Herd host.
 *
 * Store links key off this rather than the build MODE: a locally built bundle
 * (npm run build:local) compiles in production mode, so a MODE check sends every
 * dashboard "Open" to the live storefront while developing.
 */
export const isLocalAppHost = (host: string = window.location?.hostname ?? ''): boolean => {
    const hostname = normalizeHost(host);

    return hostname === 'fleetwire.test' || hostname.endsWith('.fleetwire.test');
};

export const rootPathRedirect = (): { name: string } => (
    isDashboardAppHost(window.location.hostname)
        ? {name: 'login'}
        : {name: 'company'}
);

/**
 * Query params that identify the company when creating a cart from the current host.
 *
 * - `{slug}.fleetwire.io|test|shop` → `company_slug`
 * - custom domains → `company_custom_domain`
 * - apex / infrastructure hosts → empty (server site config should already know the company)
 */
export const companyCartQueryParams = (host: string = window.location.hostname): URLSearchParams => {
    const params = new URLSearchParams();
    const hostname = normalizeHost(host);

    for (const root of FIRST_PARTY_TENANT_ROOTS) {
        if (hostname === root) {
            return params;
        }

        if (!hostname.endsWith(`.${root}`)) {
            continue;
        }

        if (isDashboardAppHost(hostname)) {
            return params;
        }

        const subdomain = hostname.slice(0, -(root.length + 1));

        if (subdomain) {
            params.set('company_slug', subdomain);
        }

        return params;
    }

    params.set('company_custom_domain', hostname);

    return params;
};
